// The UI build bundles the player, so this is the only shaka entry point the proxy player imports.
import shaka from 'shaka-player/dist/shaka-player.ui';
import 'shaka-player/dist/controls.css';
import { singletonFactory } from '@warehouse/shared/util';
import { ProxyDrmSystem, ProxyPlaybackReady, offeredLicenseServers } from '@warehouse/object-browser/core';

/** Everything a player needs from a ready playback: the manifest document and its entitlement. */
export type ProxyPlayerSource = Pick<ProxyPlaybackReady, 'manifest' | 'drm'>;

/** The Document Picture-in-Picture API, which TypeScript's DOM library does not describe yet. */
interface DocumentPictureInPicture extends EventTarget {
	readonly window: Window | null;
}

interface DocumentPictureInPictureEvent extends Event {
	readonly window: Window | null;
}

/** `Node.moveBefore`, which moves an element without taking it out of the document. Not everywhere yet. */
interface ElementWithAtomicMove extends Element {
	moveBefore?: (node: Node, child: Node | null) => void;
}

// Optional because Safari and Firefox do not implement Document Picture-in-Picture at all.
declare global {
	interface Window {
		readonly documentPictureInPicture?: DocumentPictureInPicture;
	}
}

/** The EME key system each DRM system the proxy service issues licenses for is known by. */
const KEY_SYSTEMS: Record<ProxyDrmSystem, string> = {
	widevine: 'com.widevine.alpha',
	playready: 'com.microsoft.playready',
};

/** Axinom's entitlement header. The token in the playback response entitles exactly this manifest's key ids. */
const ENTITLEMENT_HEADER = 'X-AxDRM-Message';

/** Shaka's own control bar. `language` is the audio selector — one entry per audio group, so 5.1 and 2.0 list separately. */
const CONTROL_PANEL_ELEMENTS = [
	'play_pause',
	'time_and_duration',
	'spacer',
	'mute',
	'volume',
	'language',
	'picture_in_picture',
	'fullscreen',
];

const CONTAINER_CLASS = 'nx-proxy-player';
const AUDIO_ONLY_CLASS = 'nx-proxy-player--audio-only';
const STYLE_ELEMENT_ID = 'nx-proxy-player-style';
const HOST_ELEMENT_ID = 'nx-proxy-player-host';

/**
 * One live proxy player, owned outside React because Shaka moves these nodes between documents — a
 * React-rendered container crashes on unmount once Picture-in-Picture has taken it to another window.
 */
export class ProxyPlayerSession {
	/** The element a view appends into its own slot. Shaka builds its control bar inside it. */
	readonly container: HTMLDivElement;

	readonly video: HTMLVideoElement;

	readonly player: shaka.Player;

	readonly nxObjectUuid: string;

	/** Which proxy this session plays. A refetch that only re-signs the manifest keeps it; a new proxy does not. */
	readonly revision: string;

	readonly #ui: shaka.ui.Overlay;

	#source: ProxyPlayerSource;

	#manifestUrl: string;

	#ready: Promise<void>;

	#isEntitlementFilterRegistered = false;

	readonly #placementListeners = new Set<() => void>();

	readonly #disposers: (() => void)[] = [];

	/** `onPlaybackEnded` fires when Picture-in-Picture stops — the registry decides whether that ends the preview. */
	constructor(nxObjectUuid: string, revision: string, playback: ProxyPlayerSource, onPlaybackEnded: () => void) {
		ensurePlayerStyles();
		this.nxObjectUuid = nxObjectUuid;
		this.revision = revision;
		this.#source = playback;

		// Built by hand rather than rendered, so nothing but this class can remove them.
		this.container = document.createElement('div');
		this.container.className = CONTAINER_CLASS;
		this.video = document.createElement('video');
		this.video.playsInline = true;
		this.video.setAttribute('aria-label', 'Proxy Player Video');
		this.container.appendChild(this.video);

		this.player = new shaka.Player();
		this.#ui = new shaka.ui.Overlay(this.player, this.container, this.video);
		this.#ui.configure({
			controlPanelElements: CONTROL_PANEL_ELEMENTS,
			addSeekBar: true,
			showUIAlwaysOnAudioOnly: true,
			documentPictureInPicture: { enabled: true },
		});
		this.#manifestUrl = manifestBlobUrl(playback.manifest);
		this.#ready = this.#startLoad();

		this.#watchSeparateWindow(onPlaybackEnded);
		this.#watchBrowserPip(onPlaybackEnded);
	}

	/** Settles when the latest manifest has loaded, or rejects with the Shaka error that stopped it. */
	get ready(): Promise<void> {
		return this.#ready;
	}

	/** The manifest document this session last loaded. */
	get manifest(): string {
		return this.#source.manifest;
	}

	/**
	 * Load a re-signed manifest of the same proxy into this player. The video element stays where it is, so a
	 * preview in Picture-in-Picture keeps playing there. Returns the new `ready`.
	 */
	reload(playback: ProxyPlayerSource): Promise<void> {
		URL.revokeObjectURL(this.#manifestUrl);
		this.#source = playback;
		this.#manifestUrl = manifestBlobUrl(playback.manifest);
		this.#ready = this.#startLoad();
		return this.#ready;
	}

	/** Whether Shaka has moved the container into a Picture-in-Picture window of its own. */
	isInSeparateWindow(): boolean {
		return this.container.ownerDocument !== document;
	}

	/** Whether the browser's own mini-player is showing this video. */
	isInBrowserPip(): boolean {
		return document.pictureInPictureElement === this.video;
	}

	/** Put the player where a view wants it. */
	placeIn(parent: Element): void {
		if (this.container.parentElement === parent) return;

		// Re-parenting normally removes and re-inserts, which ends the mini-player — so move atomically where it exists.
		const { moveBefore } = parent as ElementWithAtomicMove;
		if (moveBefore && this.container.isConnected && parent.isConnected) {
			try {
				moveBefore.call(parent, this.container, null);
				return;
			} catch {
				// Documents differ, or this engine rejects the move; a plain one still gets it there.
			}
		}
		parent.appendChild(this.container);
	}

	/** Tells a view the container has moved, so it can take it back into its slot. Returns the unsubscribe. */
	onPlacementChange(listener: () => void): () => void {
		this.#placementListeners.add(listener);
		return () => this.#placementListeners.delete(listener);
	}

	destroy(): void {
		this.#disposers.forEach((dispose) => dispose());
		this.#disposers.length = 0;
		this.#placementListeners.clear();
		URL.revokeObjectURL(this.#manifestUrl);
		if (this.isInSeparateWindow()) window.documentPictureInPicture?.window?.close();
		this.container.remove();
		this.#ui.destroy().catch(() => undefined);
	}

	/** A session nobody is watching yet still must not raise an unhandled rejection, so one is handled here. */
	#startLoad(): Promise<void> {
		const loading = this.#load();
		loading.catch(() => undefined);
		return loading;
	}

	async #load(): Promise<void> {
		await this.player.attach(this.video);

		// Shaka picks whichever of these the browser supports — Widevine on Chrome/Firefox, PlayReady on Edge.
		const offered = offeredLicenseServers(this.#source.drm.licenseServers);
		if (offered.length > 0) {
			const servers = Object.fromEntries(offered.map(([system, url]) => [KEY_SYSTEMS[system], url]));
			this.player.configure({ drm: { servers } });
			this.#registerEntitlementFilter();
		}
		// A blob URL carries no extension, so Shaka is told which parser to use.
		await this.player.load(this.#manifestUrl, undefined, 'application/dash+xml');
		// An audio-only proxy has no picture, so the 16:9 box collapses to a control-bar strip.
		this.container.classList.toggle(AUDIO_ONLY_CLASS, this.player.isAudioOnly());
	}

	/** Registered once, reading the latest source, so a reload's token is the one sent. */
	#registerEntitlementFilter(): void {
		if (this.#isEntitlementFilterRegistered) return;
		this.#isEntitlementFilterRegistered = true;
		// The entitlement rides on license requests only, never on media fetches.
		this.player.getNetworkingEngine()?.registerRequestFilter((requestType, request) => {
			if (requestType !== shaka.net.NetworkingEngine.RequestType.LICENSE) return;
			request.headers[ENTITLEMENT_HEADER] = this.#source.drm.token;
		});
	}

	/** Shaka opens the Picture-in-Picture window itself, so that window's own events are the only notice of it. */
	#watchSeparateWindow(onPlaybackEnded: () => void): void {
		const documentPip = window.documentPictureInPicture;
		if (!documentPip) return;

		const onEnter = (event: Event) => {
			this.#notifyPlacement();
			// `pagehide` is that window closing, which may mean the container is coming back here.
			const pipWindow = (event as DocumentPictureInPictureEvent).window;
			pipWindow?.addEventListener('pagehide', () => this.#settlePlacement(onPlaybackEnded), { once: true });
		};

		documentPip.addEventListener('enter', onEnter);
		this.#disposers.push(() => documentPip.removeEventListener('enter', onEnter));
	}

	/** The mini-player is opened and closed by the viewer, so the video element is the only thing that reports it. */
	#watchBrowserPip(onPlaybackEnded: () => void): void {
		const onEnter = () => this.#notifyPlacement();
		const onLeave = () => this.#settlePlacement(onPlaybackEnded);

		this.video.addEventListener('enterpictureinpicture', onEnter);
		this.video.addEventListener('leavepictureinpicture', onLeave);
		this.#disposers.push(() => {
			this.video.removeEventListener('enterpictureinpicture', onEnter);
			this.video.removeEventListener('leavepictureinpicture', onLeave);
		});
	}

	/** Deferred because Shaka handles the same event, and a drawer may still be showing the player anyway. */
	#settlePlacement(onPlaybackEnded: () => void): void {
		queueMicrotask(() => {
			this.#notifyPlacement();
			onPlaybackEnded();
		});
	}

	#notifyPlacement(): void {
		this.#placementListeners.forEach((listener) => listener());
	}
}

/**
 * Hands out the one proxy player there is. A session lives for as long as a view is showing it or
 * Picture-in-Picture is playing it, and both kinds count.
 */
export interface ProxyPlayerRegistry {
	/**
	 * The session playing `revision` of `nxObjectUuid`, built if there is none. Any session for another NX Object,
	 * or for another revision of this one, is destroyed first — even one in Picture-in-Picture, which would otherwise
	 * keep playing a proxy that has been replaced.
	 */
	acquire(nxObjectUuid: string, revision: string, playback: ProxyPlayerSource): ProxyPlayerSession;
	/** The view showing `session` is going away. Picture-in-Picture outlives it; anything else ends now. */
	release(session: ProxyPlayerSession): void;
	/** End whatever is playing. The registry outlives any one view, so a test is responsible for this. */
	dispose(): void;
}

export class ProxyPlayerRegistryImpl implements ProxyPlayerRegistry {
	#live: ProxyPlayerSession | undefined;

	/** Whether a view is showing the live session, as opposed to only a window playing it. */
	#shownInAView = false;

	acquire(nxObjectUuid: string, revision: string, playback: ProxyPlayerSource): ProxyPlayerSession {
		let session = this.#live;
		// One preview at a time, so a second player can never hold a second DRM license.
		if (!session || session.nxObjectUuid !== nxObjectUuid || session.revision !== revision) {
			this.dispose();
			session = new ProxyPlayerSession(nxObjectUuid, revision, playback, () => this.#endUnlessShown());
			this.#live = session;
		}
		this.#shownInAView = true;
		return session;
	}

	release(session: ProxyPlayerSession): void {
		if (this.#live !== session) return;
		this.#shownInAView = false;
		// A separate window has the container already and needs nothing from this page.
		if (session.isInSeparateWindow()) return;
		// The mini-player needs its video to stay in the document, so park it rather than let it be unmounted.
		if (session.isInBrowserPip()) {
			session.placeIn(offscreenHost());
			return;
		}
		this.dispose();
	}

	dispose(): void {
		this.#live?.destroy();
		this.#live = undefined;
		this.#shownInAView = false;
	}

	/** Picture-in-Picture has stopped: with no view left to fall back to, that is the end of the preview. */
	#endUnlessShown(): void {
		if (!this.#shownInAView) this.dispose();
	}
}

export const proxyPlayerRegistrySingleton = singletonFactory<ProxyPlayerRegistry>({
	factory: () => new ProxyPlayerRegistryImpl(),
});

/** The manifest arrives as a document rather than a URL, so it is served to Shaka as a blob. */
function manifestBlobUrl(manifest: string): string {
	return URL.createObjectURL(new Blob([manifest], { type: 'application/dash+xml' }));
}

/** Somewhere in the page for the video to sit while no view shows it — kept rendered, merely out of sight. */
function offscreenHost(): HTMLElement {
	const existing = document.getElementById(HOST_ELEMENT_ID);
	if (existing) return existing;

	const host = document.createElement('div');
	host.id = HOST_ELEMENT_ID;
	host.setAttribute('aria-hidden', 'true');
	document.body.appendChild(host);
	return host;
}

/**
 * A plain stylesheet, not styled-components: nothing renders this container, and Shaka fills the
 * Picture-in-Picture document by copying every rule in `document.styleSheets` across.
 */
function ensurePlayerStyles(): void {
	if (document.getElementById(STYLE_ELEMENT_ID)) return;

	const style = document.createElement('style');
	style.id = STYLE_ELEMENT_ID;
	style.textContent = `
		.${CONTAINER_CLASS} {
			background-color: #1b1b1b;
			overflow: hidden;
			position: relative;
			width: 100%;
			aspect-ratio: 16 / 9;
		}
		.${CONTAINER_CLASS}.${AUDIO_ONLY_CLASS} {
			aspect-ratio: auto;
			height: 112px;
		}
		/* No picture to blow up, so an audio-only proxy has no use for fullscreen. */
		.${CONTAINER_CLASS}.${AUDIO_ONLY_CLASS} .shaka-fullscreen-button {
			display: none;
		}
		#${HOST_ELEMENT_ID} {
			height: 1px;
			left: 0;
			opacity: 0;
			overflow: hidden;
			pointer-events: none;
			position: fixed;
			top: 0;
			width: 1px;
		}
		.${CONTAINER_CLASS} > video {
			height: 100%;
			inset: 0;
			position: absolute;
			width: 100%;
		}
	`;
	document.head.appendChild(style);
}
